import { Stack, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { useAtomValue } from 'jotai';
import { useMemo } from 'react';
import VerticalBarChart from '@/components/charts/VerticalBarChart';
import { YEAR_MONTH_DAY_FORMAT } from '@/constants/date.constants';
import { useFetchCaseMetrics } from '@/pages/Dashboard/DashboardCases/DashboardCases.store';
import { TICKETED_LOG_CASES_REPORTS_VERTICAL_BAR_SERIES } from '@/pages/ReportsTemplates/ReportsTemplates.constants';
import {
  reportsEndDateAtom,
  reportsStartDateAtom,
  reportsTimeFrameAtom
} from '../../ReportsTemplate.hooks';
import { getGraphCounts } from '../../ReportsTemplate.utils';
import useStyles from '../ReportsPages.styles';

const TicketedCasesGraph = ({ type }: { type: 'log' | 'network' }) => {
  const { classes } = useStyles();
  const timeframe = useAtomValue(reportsTimeFrameAtom);
  const endDate = useAtomValue(reportsEndDateAtom);
  const startDate = useAtomValue(reportsStartDateAtom);

  const minTimeFrame = timeframe < 7 ? 7 : timeframe;

  const organizationLabel =
    new URLSearchParams(window.location.search).get('organization') ?? 'pontst'; // Get organization from URL

  const minimumStartDate =
    timeframe < 7
      ? dayjs(startDate)
          .subtract(7 - timeframe, 'day')
          .format('YYYY-MM-DD')
      : startDate; // Minimum date difference for graphs over timeframes less than 7 days

  const inclusiveEndDate = dayjs(endDate)
    .add(1, 'day')
    .format(YEAR_MONTH_DAY_FORMAT); // Adding 1 day to include the whole end date

  const { data: ticketedCasesPerDay } = useFetchCaseMetrics({
    created_at: { from_date: minimumStartDate, to_date: inclusiveEndDate },
    group_by: 'day',
    ticketed: true,
    case_type: type,
    organization_label: organizationLabel
  });
  const ticketedCases = getGraphCounts(
    minTimeFrame,
    ticketedCasesPerDay,
    endDate
  );

  const ticketedCasesByDateGraph = useMemo(() => {
    return (
      <>
        <Typography className={classes.reportsPreviewSubTitle}>
          {`${minTimeFrame} Day Totals`}
        </Typography>
        <VerticalBarChart
          data={ticketedCases}
          series={TICKETED_LOG_CASES_REPORTS_VERTICAL_BAR_SERIES}
        />
      </>
    );
  }, [ticketedCases, type, minTimeFrame]);

  return (
    <Stack>
      <div
        style={{ width: '100%', height: '300px' }}
        className={classes.reportsChartContainer}
        data-testid="ticketed-alerts-by-date-graph"
      >
        {ticketedCasesByDateGraph}
      </div>
    </Stack>
  );
};
export default TicketedCasesGraph;
